import { API } from "../../shared/apiRoutes";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AddLocationResponse, DeleteRes, LocationDataResponse, LocationProps, LocationResponse, MappingRes, PayloadProps, StateRes, UpdateLocationResponse } from "../../components/settings/location/locationModule";

interface initialData {
    locationData: {
        value: LocationResponse | null,
        rows: LocationProps[],
        count: number,
        isReached: boolean,
        filter: string | null
        loading: boolean,
    },
    deleteResponse: { value: DeleteRes | null, loading: boolean, error: string }
    checkLocationMapping: { value: MappingRes | null, loading: boolean, error: string }
    error: string,
    addLocation: { value: AddLocationResponse | null, loading: boolean, error: string }
    editLocation: { value: UpdateLocationResponse | null, loading: boolean, error: string }
    getOneLocation: { value: LocationDataResponse | null, loading: boolean, error: string }
    getStates: { value: StateRes | null, loading: boolean, error: string }
    statesDropdownOptions: { [key: string]: { value: number; label: string, id: number }[] },
    countryName: string | null
}
interface locationListProps {
    storeId: number,
    data: PayloadProps | undefined
}
interface locationDeleteProps {
    storeId: number,
    data: number,
    id: number
}

//initial State
const initialState: initialData = {
    locationData: {
        value: null,
        rows: [],
        count: 0,
        isReached: false,
        filter: null,
        loading: true
    },
    deleteResponse: { value: null, loading: false, error: '' },
    checkLocationMapping: { value: null, loading: false, error: '' },
    error: '',
    addLocation: { value: null, loading: true, error: '' },
    editLocation: { value: null, loading: true, error: '' },
    getOneLocation: { value: null, loading: true, error: '' },
    getStates: { value: null, loading: true, error: '' },
    statesDropdownOptions: {},
    countryName: null
}

//getAllLocation list call
export const getAllLocationList = createAsyncThunk('location/getAllLocationList', ({ storeId, data }: locationListProps) => {
    const url = HttpRoutingService.replaceUrl(API.getLocationList, { storeId: storeId })
    return HttpRoutingService.getMethod<LocationResponse>(url, data)
})
//single delete location list
export const deleteLocationData = createAsyncThunk('location/deleteLocation', ({ storeId, data, id }: locationDeleteProps) => {
    const url = HttpRoutingService.replaceUrl(API.deleteLocation, { storeId: storeId, data: data });
    return HttpRoutingService.deleteMethod<DeleteRes>(url, { id: id });
})
//checkLocationMapping call
export const checkPosLocationMapping = createAsyncThunk('location/checkPosLocationMapping', ({ storeId, id }: { storeId: number, id: number }) => {
    const url = HttpRoutingService.replaceUrl(API.checkLocationMapping, { storeId: storeId, data: id })
    return HttpRoutingService.getMethod<MappingRes>(url)
})
//addLocation call
export const addPosLocation = createAsyncThunk('location/addPosLocation', ({ storeId, data }: { storeId: number, data: any }) => {
    const url = HttpRoutingService.replaceUrl(API.addLocation, { storeId: storeId });
    return HttpRoutingService.postMethod<AddLocationResponse>(url, data);
})
//editLocation call
export const editPosLocation = createAsyncThunk('location/editPosLocation', ({ storeId, id, data }: { storeId: number, id: number, data: any }) => {
    const url = HttpRoutingService.replaceUrl(API.updateLocation, { storeId: storeId, id: id });
    return HttpRoutingService.putMethod<UpdateLocationResponse>(url, data);
})
//getOneLocation api
export const getOnePosLocationDetails = createAsyncThunk('location/getOnePosLocationDetails', ({ storeId, id }: { storeId: number, id: number | undefined }) => {
    const url = HttpRoutingService.replaceUrl(API.getOneLocation, { storeId: storeId, id: id });
    return HttpRoutingService.getMethod<LocationDataResponse>(url);
})
//getStates api
export const getPosStateValues = createAsyncThunk('location/getPosStateValues', (name: string) => {
    return HttpRoutingService.getMethod<StateRes>('countries/' + name);
})

export const LocationSlice = createSlice({
    name: 'location',
    initialState: initialState,
    reducers: {
        setCountryName: (state, action: PayloadAction<string>) => {
            state.countryName = action.payload
        }
    },

    extraReducers: (builder) => {
        builder.addCase(getAllLocationList?.pending, (state) => {
            state.locationData.loading = true
        })
        builder.addCase(getAllLocationList?.fulfilled, (state, action) => {
            state.locationData.loading = false;
            state.locationData.value = action.payload;

            const offset = action.meta.arg.data?.offset ?? 0;
            const limit = action.meta.arg.data?.limit ?? 10;
            const incoming = action.payload?.storeLocation ?? [];

            if (offset > 0) {
                state.locationData.rows = [...state.locationData.rows, ...incoming];
            } else {
                state.locationData.rows = incoming;
                state.locationData.count = action.payload?.totalCount ?? 0;
            }

            state.locationData.isReached = incoming.length < limit;
            state.locationData.filter = JSON.stringify({
                searchText: action.meta.arg.data?.searchText,
                filterData: action.meta.arg.data?.filterData,
            });
        })
        builder.addCase(getAllLocationList?.rejected, (state, action) => {
            state.locationData.loading = false
            state.error = action?.error?.message ? action?.error?.message : 'Error in get locationList'
        })
        builder.addCase(deleteLocationData?.pending, (state) => {
            state.deleteResponse.loading = true
        })
        builder.addCase(deleteLocationData?.fulfilled, (state, action: PayloadAction<DeleteRes>) => {
            state.deleteResponse.loading = false,
                state.deleteResponse.value = action?.payload
        })
        builder.addCase(deleteLocationData?.rejected, (state, action) => {
            state.deleteResponse.loading = false
            state.deleteResponse.error = action?.error?.message ? action?.error?.message : 'Error in delete'
        })
        builder.addCase(checkPosLocationMapping?.pending, (state) => {
            state.checkLocationMapping.loading = true
        })
        builder.addCase(checkPosLocationMapping?.fulfilled, (state, action: PayloadAction<MappingRes>) => {
            state.checkLocationMapping.loading = false
            state.checkLocationMapping.value = action?.payload
        })
        builder.addCase(checkPosLocationMapping?.rejected, (state, action) => {
            state.checkLocationMapping.loading = false
            state.checkLocationMapping.error = action?.error?.message ? action?.error?.message : 'Error in check Location mapping'
        })
        builder.addCase(addPosLocation?.pending, (state) => {
            state.addLocation.loading = true
        })
        builder.addCase(addPosLocation?.fulfilled, (state, action: PayloadAction<AddLocationResponse>) => {
            state.addLocation.loading = false
            state.addLocation.value = action?.payload
        })
        builder.addCase(addPosLocation?.rejected, (state, action) => {
            state.addLocation.loading = false
            state.addLocation.error = action?.error?.message ? action?.error?.message : 'Error in addLocation'
        })
        builder.addCase(editPosLocation?.pending, (state) => {
            state.editLocation.loading = true
        })
        builder.addCase(editPosLocation?.fulfilled, (state, action: PayloadAction<UpdateLocationResponse>) => {
            state.editLocation.loading = false
            state.editLocation.value = action?.payload
        })
        builder.addCase(editPosLocation?.rejected, (state, action) => {
            state.editLocation.loading = false
            state.editLocation.error = action?.error?.message ? action?.error?.message : 'Error in editLocation'
        })
        builder.addCase(getOnePosLocationDetails?.pending, (state) => {
            state.getOneLocation.loading = true
            // clear the previously opened location so the form never shows/saves another location's data
            state.getOneLocation.value = null
            state.getOneLocation.error = ''
        })
        builder.addCase(getOnePosLocationDetails?.fulfilled, (state, action: PayloadAction<LocationDataResponse>) => {
            state.getOneLocation.loading = false
            console.log(JSON.stringify(action?.payload, undefined, 4));

            state.getOneLocation.value = action?.payload
        })
        builder.addCase(getOnePosLocationDetails?.rejected, (state, action) => {
            state.getOneLocation.loading = false
            state.getOneLocation.value = null
            state.getOneLocation.error = action?.error?.message ? action?.error?.message : 'Error in getOneLocationDetails'
        })
        builder.addCase(getPosStateValues?.pending, (state) => {
            state.getStates.loading = true
        })
        builder.addCase(getPosStateValues?.fulfilled, (state, action) => {
            state.getStates.loading = false
            state.getStates.value = action?.payload
            state.countryName = action.meta.arg
            state.statesDropdownOptions[action.meta.arg] = (action.payload?.states ?? []).map(item => ({
                value: item.id,
                id: item.id,
                label: item.name,
            }))
        })
        builder.addCase(getPosStateValues?.rejected, (state, action) => {
            state.getStates.loading = false
            state.getStates.error = action?.error?.message ? action?.error?.message : 'Error in getStateValues'
        })
    }
})

export const { setCountryName } = LocationSlice?.actions
export default LocationSlice?.reducer